import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogOut, Plus, Pencil, Trash2, RotateCcw, PackageOpen, ChevronUp, ChevronDown, Star, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, fmt, formatApiErrorDetail, imgUrl } from "@/lib/api";
import { SiteSections } from "@/components/SiteSections";
import { BooksDashboard } from "@/components/books/BooksDashboard";
import { BooksSales } from "@/components/books/BooksSales";
import { BooksExpenses } from "@/components/books/BooksExpenses";
import { BooksSettings } from "@/components/books/BooksSettings";

const EMPTY_FORM = {
  name: "", tagline: "", flavor_category: "Classic Dill", price: "", size: "16 oz Mason Jar",
  heat_level: 0, crunch_score: "", tang_score: "", sweet_score: "", image: "",
  description: "", ingredients: "", badge: "", inventory_status: "In Stock", featured: false,
  stock: "",
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 block mb-1.5">{label}</span>
    {children}
  </label>
);

const inputCls =
  "w-full bg-white border border-parchborder rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-dill transition-colors";

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("pmok_admin_token", data.token);
      onLogin();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-parchment flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-sm bg-white rounded-2xl border border-parchborder p-8 shadow-xl"
      >
        <div className="flex items-center gap-2.5 mb-2">
          <img src="/logo.png" alt="Pickle Me OK! logo" className="w-10 h-10 rounded-full object-cover ring-2 ring-dill/15" />
          <h1 className="font-serif font-black text-xl text-dill">Pantry Portal</h1>
        </div>
        <p className="text-xs text-ink/50 mb-6">Owner access only. Mind the brine.</p>
        <div className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            required
            className={inputCls}
            data-testid="admin-email-input"
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            className={inputCls}
            data-testid="admin-password-input"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          className="mt-5 w-full bg-dill hover:bg-terra disabled:opacity-60 text-parchment font-bold rounded-full py-3 text-sm uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
          data-testid="admin-login-submit-button"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}
          Unlock the Pantry
        </button>
        <Link to="/" className="block text-center mt-4 text-xs text-ink/40 hover:text-terra transition-colors" data-testid="admin-back-home-link">
          ← Back to the shop
        </Link>
      </motion.form>
    </div>
  );
}

function ProductEditor({ initial, onSave, onCancel, busy }) {
  const [form, setForm] = useState(initial);
  const [imgBusy, setImgBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const uploadImage = async (file) => {
    if (!file) return;
    setImgBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/products/image", fd);
      setForm((f) => ({ ...f, image: data.path }));
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "That photo couldn't be uploaded");
    } finally {
      setImgBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="bg-parchdeep/60 border border-parchborder rounded-2xl p-6 space-y-4" data-testid="admin-product-editor">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Jar name *">
          <input value={form.name} onChange={set("name")} required className={inputCls} data-testid="admin-field-name" />
        </Field>
        <Field label="Tagline">
          <input value={form.tagline} onChange={set("tagline")} className={inputCls} data-testid="admin-field-tagline" />
        </Field>
        <Field label="Flavor category">
          <input value={form.flavor_category} onChange={set("flavor_category")} className={inputCls} data-testid="admin-field-category" />
        </Field>
        <Field label="Price (USD) *">
          <input type="number" step="0.01" min="0.5" value={form.price} onChange={set("price")} required className={inputCls} data-testid="admin-field-price" />
        </Field>
        <Field label="Size">
          <input value={form.size} onChange={set("size")} className={inputCls} data-testid="admin-field-size" />
        </Field>
        <Field label="Heat level (0–5)">
          <input type="number" min="0" max="5" value={form.heat_level} onChange={set("heat_level")} className={inputCls} data-testid="admin-field-heat" />
        </Field>
        <Field label="Badge (e.g. Bestseller)">
          <input value={form.badge} onChange={set("badge")} className={inputCls} data-testid="admin-field-badge" />
        </Field>
        <Field label="Stock note">
          <input value={form.inventory_status} onChange={set("inventory_status")} className={inputCls} data-testid="admin-field-stock" />
        </Field>
        <Field label="Inventory count (blank = unlimited)">
          <input type="number" min="0" step="1" value={form.stock ?? ""} onChange={set("stock")} placeholder="e.g. 12" className={inputCls} data-testid="admin-field-inventory" />
          <p className="text-[11px] text-ink/40 mt-1">Counts down with every order — at 0 the jar marks itself Sold Out, and restocking here brings it back.</p>
        </Field>
      </div>
      <div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/50 block mb-1.5">Jar photo</span>
        <div className="flex flex-wrap items-center gap-4">
          {form.image && (
            <img
              src={imgUrl(form.image)}
              alt="Jar photo preview"
              className="w-16 h-16 rounded-lg object-cover border border-parchborder bg-parchdeep"
              data-testid="admin-image-preview"
            />
          )}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={imgBusy}
            className="flex items-center gap-2 bg-dill hover:bg-terra disabled:opacity-60 text-parchment font-semibold rounded-full px-5 py-2.5 text-sm transition-colors"
            data-testid="admin-image-upload-button"
          >
            {imgBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {form.image ? "Change Photo" : "Upload Photo"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => uploadImage(e.target.files?.[0])}
            data-testid="admin-image-file-input"
          />
        </div>
        <p className="text-[11px] text-ink/40 mt-1.5">
          Pick a photo from your phone or computer — it's automatically sized and saved with the jar.
        </p>
      </div>
      <Field label="Description">
        <textarea value={form.description} onChange={set("description")} rows={3} className={inputCls} data-testid="admin-field-description" />
      </Field>
      <div className="grid grid-cols-3 gap-4">
        <Field label="Crunch /10">
          <input value={form.crunch_score} onChange={set("crunch_score")} placeholder="9.9 / 10" className={inputCls} data-testid="admin-field-crunch" />
        </Field>
        <Field label="Tang /10">
          <input value={form.tang_score} onChange={set("tang_score")} placeholder="9.4 / 10" className={inputCls} data-testid="admin-field-tang" />
        </Field>
        <Field label="Sweet /10">
          <input value={form.sweet_score} onChange={set("sweet_score")} placeholder="1.2 / 10" className={inputCls} data-testid="admin-field-sweet" />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-sm text-ink/70">
        <input type="checkbox" checked={form.featured} onChange={set("featured")} className="accent-dill" data-testid="admin-field-featured" />
        Feature this jar at the top of the shelf
      </label>
      <div className="flex gap-3 pt-2">
        <button
          onClick={() => onSave({
            ...form,
            price: parseFloat(form.price),
            heat_level: parseInt(form.heat_level) || 0,
            stock: form.stock === "" || form.stock === null || form.stock === undefined ? null : Math.max(0, parseInt(form.stock) || 0),
          })}
          disabled={busy || !form.name || !form.price}
          className="bg-dill hover:bg-terra disabled:opacity-50 text-parchment font-bold rounded-full px-6 py-2.5 text-sm transition-colors flex items-center gap-2"
          data-testid="admin-product-save-button"
        >
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} Save Jar
        </button>
        <button onClick={onCancel} className="text-sm text-ink/50 hover:text-terra transition-colors" data-testid="admin-product-cancel-button">
          Cancel
        </button>
      </div>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | product object
  const [busy, setBusy] = useState(false);

  const loadProducts = () => api.get("/products").then((r) => setProducts(r.data));
  const loadOrders = () => api.get("/admin/orders").then((r) => setOrders(r.data));
  const loadReviews = () => api.get("/admin/reviews").then((r) => setReviews(r.data));

  useEffect(() => {
    const token = localStorage.getItem("pmok_admin_token");
    if (!token) {
      setChecking(false);
      return;
    }
    api
      .get("/auth/me")
      .then(() => setAuthed(true))
      .catch(() => localStorage.removeItem("pmok_admin_token"))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (authed) {
      loadProducts().catch(() => toast.error("Could not load products"));
      loadOrders().catch(() => toast.error("Could not load orders"));
      loadReviews().catch(() => toast.error("Could not load reviews"));
    }
  }, [authed]);

  // refetch orders whenever the Orders tab is opened (book sales mirror into it)
  useEffect(() => {
    if (authed && tab === "orders") {
      loadOrders().catch(() => {});
    }
  }, [tab, authed]);

  const saveProduct = async (form) => {
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/admin/products", form);
        toast.success("New jar added to the shelf");
      } else {
        await api.put(`/admin/products/${editing.id}`, form);
        toast.success("Jar updated");
      }
      setEditing(null);
      await loadProducts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const toggleSoldOut = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}/sold-out`, { sold_out: !p.sold_out });
      await loadProducts();
      toast.success(`${p.name} marked ${p.sold_out ? "in stock" : "sold out"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleComingSoon = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}/coming-soon`, { coming_soon: !p.coming_soon });
      await loadProducts();
      toast.success(`${p.name} ${p.coming_soon ? "is back on the shelf" : "marked coming soon"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const moveProduct = async (id, dir) => {
    const ids = products.map((p) => p.id);
    const i = ids.indexOf(id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    try {
      await api.put("/admin/products/reorder", { ordered_ids: ids });
      await loadProducts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteProduct = async (id) => {
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Jar removed");
      await loadProducts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const resetCatalog = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/products/reset");
      setProducts(data);
      setEditing(null);
      toast.success("Sample catalog restored");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const setOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/admin/orders/${orderId}`, { status });
      toast.success(`Order ${orderId} marked ${status}`);
      await loadOrders();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleReviewApproval = async (r) => {
    try {
      await api.put(`/admin/reviews/${r.id}`, { approved: !r.approved });
      toast.success(r.approved ? "Review unpublished" : "Review is now live on the site");
      await loadReviews();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteReview = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      await loadReviews();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteOrder = async (orderId) => {
    try {
      await api.delete(`/admin/orders/${orderId}`);
      toast.success(`Order ${orderId} deleted forever`);
      await loadOrders();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const activeOrders = orders.filter((o) => o.status === "awaiting_payment" || o.status === "paid");
  const completedOrders = orders.filter((o) => o.status === "fulfilled");
  const archivedOrders = orders.filter((o) => o.status === "cancelled" || o.status === "deleted");

  const logout = () => {
    localStorage.removeItem("pmok_admin_token");
    setAuthed(false);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-parchment flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-dill" />
      </div>
    );
  }

  if (!authed) return <LoginForm onLogin={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen bg-parchment" data-testid="admin-dashboard">
      <header className="border-b border-parchborder bg-parchment/90 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.png" alt="Pickle Me OK! logo" className="w-9 h-9 rounded-full object-cover ring-2 ring-dill/15" />
            <span className="font-serif font-black text-lg text-dill">Pantry Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-xs font-mono uppercase tracking-widest text-ink/50 hover:text-terra transition-colors" data-testid="admin-view-shop-link">
              View Shop
            </Link>
            <button onClick={logout} className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-ink/50 hover:text-terra transition-colors" data-testid="admin-logout-button">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap gap-2 mb-8">
          {["products", "orders", "reviews", "sections", "dash", "sales", "expenses", "settings"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`rounded-full px-5 py-2.5 text-xs font-mono uppercase tracking-widest transition-colors ${
                tab === t ? "bg-dill text-parchment" : "text-ink/55 hover:text-terra border border-parchborder"
              }`}
              data-testid={`admin-tab-${t}`}
            >
              {t === "products"
                ? `The Shelf (${products.length})`
                : t === "orders"
                  ? `Orders (${orders.filter((o) => o.status === "awaiting_payment" || o.status === "paid").length})`
                  : t === "reviews"
                    ? reviews.some((r) => !r.approved)
                      ? `Reviews (${reviews.filter((r) => !r.approved).length} new)`
                      : `Reviews (${reviews.length})`
                    : t === "sections"
                      ? "Site Sections"
                      : { dash: "Overview", sales: "Sales", expenses: "Expenses", settings: "Settings" }[t]}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <div className="space-y-5" data-testid="admin-products-panel">
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setEditing("new")}
                className="flex items-center gap-2 bg-terra hover:bg-terra-hover text-parchment font-semibold rounded-full px-5 py-2.5 text-sm transition-colors"
                data-testid="admin-add-product-button"
              >
                <Plus className="w-4 h-4" /> Add New Jar
              </button>
              <button
                onClick={resetCatalog}
                disabled={busy}
                className="flex items-center gap-2 border border-parchborder hover:border-terra hover:text-terra text-ink/60 font-semibold rounded-full px-5 py-2.5 text-sm transition-colors"
                data-testid="admin-reset-catalog-button"
              >
                <RotateCcw className="w-4 h-4" /> Reset Sample Catalog
              </button>
            </div>

            {editing === "new" && (
              <ProductEditor initial={EMPTY_FORM} onSave={saveProduct} onCancel={() => setEditing(null)} busy={busy} />
            )}

            {products.map((p, idx) => (
              <div key={p.id} className="bg-white border border-parchborder rounded-2xl p-4 flex flex-col gap-4" data-testid={`admin-product-row-${p.id}`}>
                <div className="flex items-center gap-4">
                  <img src={imgUrl(p.image)} alt={p.name} className="w-14 h-14 rounded-lg object-cover shrink-0 bg-parchdeep" />
                  <div className="flex-1 min-w-0">
                    <p className="font-serif font-bold text-dill truncate">{p.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-ink/45">
                      {p.flavor_category} • {fmt(p.price)} • {p.stock != null ? `${p.stock} left` : p.inventory_status}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => toggleSoldOut(p)}
                      className={`font-mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-full border transition-colors ${
                        p.sold_out
                          ? "bg-terra/10 text-terra border-terra/40 hover:bg-terra hover:text-parchment"
                          : "bg-dill/10 text-dill border-dill/30 hover:bg-terra/10 hover:text-terra hover:border-terra/40"
                      }`}
                      data-testid={`admin-soldout-toggle-${p.id}`}
                    >
                      {p.sold_out ? "Sold Out" : "In Stock"}
                    </button>
                    <button
                      onClick={() => toggleComingSoon(p)}
                      className={`font-mono text-[10px] uppercase tracking-widest px-3 py-2 rounded-full border transition-colors ${
                        p.coming_soon
                          ? "bg-gold/20 text-gold border-gold/50 hover:bg-gold hover:text-white"
                          : "border-parchborder text-ink/50 hover:border-gold hover:text-gold"
                      }`}
                      data-testid={`admin-comingsoon-toggle-${p.id}`}
                    >
                      Coming Soon
                    </button>
                    <button
                      onClick={() => moveProduct(p.id, -1)}
                      disabled={idx === 0}
                      className="p-2 rounded-full border border-parchborder hover:border-dill hover:text-dill transition-colors disabled:opacity-30 disabled:hover:border-parchborder disabled:hover:text-current"
                      aria-label="Move product up"
                      data-testid={`admin-move-up-${p.id}`}
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => moveProduct(p.id, 1)}
                      disabled={idx === products.length - 1}
                      className="p-2 rounded-full border border-parchborder hover:border-dill hover:text-dill transition-colors disabled:opacity-30 disabled:hover:border-parchborder disabled:hover:text-current"
                      aria-label="Move product down"
                      data-testid={`admin-move-down-${p.id}`}
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setEditing(editing?.id === p.id ? null : p)}
                      className="p-2 rounded-full border border-parchborder hover:border-dill hover:text-dill transition-colors"
                      aria-label="Edit product"
                      data-testid={`admin-edit-product-${p.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => deleteProduct(p.id)}
                      className="p-2 rounded-full border border-parchborder hover:border-terra hover:text-terra transition-colors"
                      aria-label="Delete product"
                      data-testid={`admin-delete-product-${p.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {editing && editing !== "new" && editing.id === p.id && (
                  <ProductEditor initial={{ ...EMPTY_FORM, ...p }} onSave={saveProduct} onCancel={() => setEditing(null)} busy={busy} />
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "sections" && <SiteSections />}
        {tab === "dash" && <BooksDashboard />}
        {tab === "sales" && <BooksSales />}
        {tab === "expenses" && <BooksExpenses />}
        {tab === "settings" && <BooksSettings />}

        {tab === "reviews" && (
          <div className="space-y-4" data-testid="admin-reviews-panel">
            {reviews.length === 0 && (
              <div className="text-center py-20 bg-white rounded-2xl border border-parchborder" data-testid="admin-reviews-empty">
                <Star className="w-10 h-10 text-ink/20 mx-auto mb-3" />
                <p className="font-serif text-xl text-dill/60 italic">No reviews yet.</p>
                <p className="text-sm text-ink/50 mt-1">Customer reviews will land here for your approval.</p>
              </div>
            )}
            {reviews.map((r) => (
              <div key={r.id} className="bg-white border border-parchborder rounded-2xl p-5" data-testid={`admin-review-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                  <div>
                    <p className="font-serif font-bold text-dill">{r.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-ink/45">
                      {new Date(r.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="flex items-center gap-0.5" data-testid={`admin-review-stars-${r.id}`}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-gold text-gold" : "text-ink/15"}`} />
                      ))}
                    </span>
                    <span
                      className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full ${
                        r.approved ? "bg-dill/10 text-dill" : "bg-gold/20 text-gold"
                      }`}
                      data-testid={`admin-review-status-${r.id}`}
                    >
                      {r.approved ? "Live" : "Pending"}
                    </span>
                    <button
                      onClick={() => toggleReviewApproval(r)}
                      className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full transition-colors ${
                        r.approved
                          ? "border border-parchborder text-ink/50 hover:border-terra hover:text-terra"
                          : "bg-dill hover:bg-terra text-parchment"
                      }`}
                      data-testid={`admin-review-approve-${r.id}`}
                    >
                      {r.approved ? "Unpublish" : "Approve"}
                    </button>
                    <button
                      onClick={() => deleteReview(r.id)}
                      className="p-2 rounded-full border border-parchborder hover:border-terra hover:text-terra transition-colors"
                      aria-label="Delete review"
                      data-testid={`admin-review-delete-${r.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <p className="text-sm text-ink/70 italic">“{r.text}”</p>
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-8" data-testid="admin-orders-panel">
            <section data-testid="active-orders-section">
              <h2 className="font-serif font-bold text-xl text-dill mb-4">Active Orders</h2>
              {activeOrders.length === 0 && (
                <div className="text-center py-16 bg-white rounded-2xl border border-parchborder" data-testid="admin-orders-empty">
                  <PackageOpen className="w-10 h-10 text-ink/20 mx-auto mb-3" />
                  <p className="font-serif text-xl text-dill/60 italic">No active orders.</p>
                  <p className="text-sm text-ink/50 mt-1">New orders land here the moment a customer checks out.</p>
                </div>
              )}
              <div className="space-y-4">
                {activeOrders.map((o) => (
                  <div key={o.id} className="bg-white border border-parchborder rounded-2xl p-5" data-testid={`admin-order-${o.id}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                      <div>
                        <p className="font-mono text-sm font-bold text-dill">#{o.id}</p>
                        <p className="text-xs text-ink/50">
                          {o.customer_name}
                          {[o.customer_phone, o.customer_email].filter(Boolean).length > 0 &&
                            ` • ${[o.customer_phone, o.customer_email].filter(Boolean).join(" • ")}`}{" "}
                          • {new Date(o.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-terra/10 text-terra"
                          data-testid={`admin-order-method-${o.id}`}
                        >
                          {o.payment_method === "cashapp"
                            ? "CashApp"
                            : o.payment_method === "stripe"
                              ? "Card"
                              : o.payment_method === "applepay"
                                ? "Apple Pay"
                                : o.payment_method === "venmo"
                                  ? "Venmo"
                                  : o.payment_method === "in_person"
                                    ? "In Person"
                                    : o.payment_method
                                      ? o.payment_method.charAt(0).toUpperCase() + o.payment_method.slice(1)
                                      : "Card (old)"}
                        </span>
                        <span
                          className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full ${
                            o.status === "paid" ? "bg-gold/20 text-gold" : "bg-ink/5 text-ink/40"
                          }`}
                          data-testid={`admin-order-status-${o.id}`}
                        >
                          {o.status.replace("_", " ")}
                        </span>
                        {o.status === "awaiting_payment" && (
                          <button
                            onClick={() => setOrderStatus(o.id, "paid")}
                            className="font-mono text-[10px] uppercase tracking-widest bg-gold hover:bg-terra text-white px-3 py-1.5 rounded-full transition-colors"
                            data-testid={`admin-order-mark-paid-${o.id}`}
                          >
                            Mark Paid
                          </button>
                        )}
                        {o.status === "paid" && (
                          <button
                            onClick={() => setOrderStatus(o.id, "fulfilled")}
                            className="font-mono text-[10px] uppercase tracking-widest bg-dill hover:bg-terra text-parchment px-3 py-1.5 rounded-full transition-colors"
                            data-testid={`admin-order-status-toggle-${o.id}`}
                          >
                            Mark Fulfilled
                          </button>
                        )}
                        <button
                          onClick={() => setOrderStatus(o.id, "cancelled")}
                          className="font-mono text-[10px] uppercase tracking-widest border border-parchborder text-ink/45 hover:border-terra hover:text-terra px-3 py-1.5 rounded-full transition-colors"
                          data-testid={`admin-order-cancel-${o.id}`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                    <div className="text-sm text-ink/70 space-y-1">
                      {o.items.map((i) => (
                        <p key={i.product_id}>
                          {i.quantity} × {i.name} — {fmt(i.unit_price * i.quantity)}
                        </p>
                      ))}
                      <p className="font-semibold text-ink pt-1">
                        {o.fulfillment === "pickup" ? "Local pickup" : `Delivery: ${[o.delivery_address, o.delivery_city].filter(Boolean).join(", ") || "—"}`} • Total {fmt(o.total)}
                        {o.promo_code ? ` • Promo ${o.promo_code}${o.discount ? ` (−${fmt(o.discount)})` : ""}` : ""}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <section className="bg-white border border-parchborder rounded-2xl p-5 self-start" data-testid="completed-orders-card">
                <h3 className="font-serif font-bold text-lg text-dill mb-2">Completed Orders</h3>
                {completedOrders.length === 0 && (
                  <p className="text-sm text-ink/50 italic py-6 text-center" data-testid="completed-orders-empty">Nothing fulfilled yet.</p>
                )}
                {completedOrders.map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-3 border-b border-parchborder/60 last:border-0 py-3" data-testid={`admin-order-${o.id}`}>
                    <div className="min-w-0">
                      <p className="font-mono text-xs font-bold text-dill">
                        #{o.id}
                        {o.manual && (
                          <span className="ml-2 font-mono text-[9px] uppercase tracking-widest bg-gold/20 text-gold px-2 py-0.5 rounded-full" data-testid={`admin-order-manual-${o.id}`}>
                            Manual
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-ink/50 truncate">
                        {o.customer_name} • {fmt(o.total)} • {new Date(o.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => setOrderStatus(o.id, "deleted")}
                      className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-parchborder text-ink/50 hover:border-terra hover:text-terra transition-colors shrink-0"
                      data-testid={`admin-order-delete-${o.id}`}
                    >
                      Delete
                    </button>
                  </div>
                ))}
              </section>

              <section className="bg-white border border-parchborder rounded-2xl p-5 self-start" data-testid="archived-orders-card">
                <h3 className="font-serif font-bold text-lg text-dill mb-2">Deleted/Cancelled Orders</h3>
                {archivedOrders.length === 0 && (
                  <p className="text-sm text-ink/50 italic py-6 text-center" data-testid="archived-orders-empty">Nothing here — good.</p>
                )}
                {archivedOrders.map((o) => (
                  <div key={o.id} className="flex items-center justify-between gap-3 border-b border-parchborder/60 last:border-0 py-3" data-testid={`admin-order-${o.id}`}>
                    <div className="min-w-0">
                      <p className="font-mono text-xs font-bold text-dill">#{o.id}</p>
                      <p className="text-xs text-ink/50 truncate">
                        {o.customer_name} • {fmt(o.total)} • {new Date(o.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span
                        className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full bg-ink/5 text-ink/40"
                        data-testid={`admin-order-status-${o.id}`}
                      >
                        {o.status}
                      </span>
                      <button
                        onClick={() => setOrderStatus(o.id, "awaiting_payment")}
                        className="font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border border-parchborder text-ink/50 hover:border-dill hover:text-dill transition-colors"
                        data-testid={`admin-order-restore-${o.id}`}
                      >
                        Restore
                      </button>
                      <button
                        onClick={() => deleteOrder(o.id)}
                        className="p-2 rounded-full border border-parchborder hover:border-terra hover:text-terra transition-colors"
                        aria-label="Delete order forever"
                        data-testid={`admin-order-delete-forever-${o.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </section>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
